import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ArrowUpRight, ChevronDown } from 'lucide-react';

const services = [
  {
    id: 'travel',
    number: '1',
    title: 'Travel Staffing',
    description: 'We provide experienced professionals for short-term assignments across regions, helping facilities maintain quality care during staff shortages, peak seasons, or special projects.',
    image: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800&q=80',
    thumbnail: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=400&q=80',
  },
  {
    id: 'local',
    number: '2',
    title: 'Local Contracts & Per Diem',
    description: 'Flexible staffing solutions for local needs—whether it\'s daily shift coverage or short-term contracts—ensuring seamless support without long-term commitment.',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80',
    thumbnail: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&q=80',
  },
  {
    id: 'direct',
    number: '3',
    title: 'Direct Hire',
    description: 'Our direct placement service connects healthcare organizations with qualified candidates who are the right fit for permanent roles, reducing hiring time and turnover.',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80',
    thumbnail: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&q=80',
  },
  {
    id: 'managed',
    number: '4',
    title: 'Managed Services',
    description: 'A comprehensive approach to workforce management, including vendor coordination, scheduling, and reporting—designed to streamline operations and improve efficiency.',
    image: 'https://images.unsplash.com/photo-1565688527174-775059ac429c?w=800&q=80',
    thumbnail: 'https://images.unsplash.com/photo-1565688527174-775059ac429c?w=400&q=80',
  },
  {
    id: 'crisis',
    number: '5',
    title: 'Crisis & Rapid Response Staffing',
    description: 'When urgency strikes, we deliver fast and reliable staffing support to help facilities respond to emergencies, disasters, or sudden demand increases.',
    image: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800&q=80',
    thumbnail: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=400&q=80',
  },
  {
    id: 'telehealth',
    number: '6',
    title: 'Telehealth Support Staffing',
    description: 'From clinical professionals to administrative support, we provide specialized staff to strengthen and scale your telehealth services effectively.',
    image: 'https://static.prod-images.emergentagent.com/jobs/3c79fc15-7acb-4945-a01c-dd7a9cf21362/images/18ea3b31b2e10084b375e030e5911b73f81425b2ccc4f82c702fc0036c076b5c.png',
    thumbnail: 'https://static.prod-images.emergentagent.com/jobs/3c79fc15-7acb-4945-a01c-dd7a9cf21362/images/18ea3b31b2e10084b375e030e5911b73f81425b2ccc4f82c702fc0036c076b5c.png',
  },
];

export const Services = () => {
  const [activeService, setActiveService] = useState(services[0]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sectionRef, sectionVisible] = useScrollAnimation();

  const handleServiceSelect = (service) => {
    setActiveService(service);
    setMobileMenuOpen(false);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="section-padding bg-[#000080]"
    >
      <div className="container-width">
        <div
          ref={sectionRef}
          className={`animate-on-scroll ${sectionVisible ? 'visible' : ''} rounded-2xl overflow-hidden`}
        >
          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Mobile Service Selector */}
            <div className="p-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-full bg-[#366FED] text-white rounded-lg p-4 flex items-center justify-between"
                data-testid="mobile-service-selector"
              >
                <div className="flex items-center gap-3">
                  <div className="w-16 h-10 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={activeService.thumbnail}
                      alt={activeService.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-semibold text-sm">
                    {activeService.number}. {activeService.title}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Mobile Dropdown Menu */}
            {mobileMenuOpen && (
              <div className="mt-2 space-y-2 bg-[#000066] rounded-lg p-2">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    data-testid={`mobile-service-tab-${service.id}`}
                    className={`w-full text-left rounded-lg p-3 flex items-center gap-3 transition-colors ${
                      activeService.id === service.id
                        ? 'bg-[#366FED] text-white'
                        : 'text-white hover:bg-[#000099]'
                    }`}
                  >
                    <div className="w-12 h-8 rounded overflow-hidden flex-shrink-0">
                      <img
                        src={service.thumbnail}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="font-medium text-sm">
                      {service.number}. {service.title}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Content Display */}
          <div className="relative min-h-[400px]">
            <div className="absolute inset-0">
              <img
                src={activeService.image}
                alt={activeService.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#000080] via-[#000080]/60 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                {activeService.title}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                {activeService.description}
              </p>
              <div className="flex justify-end">
                <Link 
                  to="/find-talent" 
                  className="w-10 h-10 rounded-full bg-[#366FED] flex items-center justify-center hover:bg-[#2860DE] transition-colors"
                  data-testid="services-cta-mobile"
                >
                  <ArrowUpRight className="w-5 h-5 text-white" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop/Tablet Layout */}
        <div className="hidden lg:flex flex-row min-h-[600px]">
          {/* Left Sidebar - Service Tabs */}
          <div className="w-[380px] flex-shrink-0 p-6 space-y-3">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => setActiveService(service)}
                data-testid={`service-tab-${service.id}`}
                className={`w-full text-left rounded-lg overflow-hidden transition-all duration-300 ${
                  activeService.id === service.id
                    ? 'bg-[#366FED] text-white'
                    : 'bg-[#000066] text-white hover:bg-[#000099]'
                }`}
              >
                <div className="flex items-center gap-3 p-3">
                  <div className="w-24 h-14 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={service.thumbnail}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-medium text-sm leading-tight">
                    {service.number}. {service.title}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Right Content - Active Service Display */}
          <div className="flex-1 relative">
            <div className="absolute inset-0">
              <img
                src={activeService.image}
                alt={activeService.title}
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="service-main-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#000080]/90 via-[#000080]/40 to-transparent" />
            </div>

            <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden xl:block">
              <span 
                className="text-white/30 text-sm font-medium tracking-[0.3em] uppercase"
                style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              >
                {activeService.title}
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-10">
              <div className="max-w-2xl">
                <h3 
                  className="text-3xl font-semibold text-white mb-4"
                  data-testid="service-title"
                >
                  {activeService.title}
                </h3>
                <p 
                  className="text-white/80 text-lg leading-relaxed mb-6"
                  data-testid="service-description"
                >
                  {activeService.description}
                </p>
                <div className="flex justify-end">
                  <Link 
                    to="/find-talent" 
                    className="w-10 h-10 rounded-full bg-[#366FED] flex items-center justify-center hover:bg-[#2860DE] transition-colors"
                    data-testid="services-cta-desktop"
                  >
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default Services;
